import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiBlob, saveBlob } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';
import { formatMinor } from '../../lib/money';

interface Run {
  id: string;
  period: string;
  status: 'draft' | 'approved' | 'paid' | 'cancelled';
  staffCount: number;
  grossMinor: number;
  netMinor: number;
}
interface RunDetail extends Run {
  currency: string;
  items: {
    id: string;
    staffName: string;
    grossMinor: number;
    lopDays: number;
    lopMinor: number;
    deductionsMinor: number;
    netMinor: number;
    unmarkedDays: number;
  }[];
}

export function PayrollTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState('');
  const [selected, setSelected] = useState('');
  const [periodError, setPeriodError] = useState<string | null>(null);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [method, setMethod] = useState('bank_transfer');
  const [message, setMessage] = useState<string | null>(null);

  const runs = useQuery({
    queryKey: ['hr', schoolId, 'runs'],
    queryFn: () => api<{ currency: string; runs: Run[] }>(`/schools/${schoolId}/payroll-runs`),
  });
  const detail = useQuery({
    queryKey: ['hr', schoolId, 'run', selected],
    queryFn: () => api<RunDetail>(`/schools/${schoolId}/payroll-runs/${selected}`),
    enabled: !!selected,
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['hr', schoolId] });
  const fail = (e: unknown) => setMessage(errorMessage(e));
  const done = () => {
    setMessage(null);
    refresh();
  };
  const create = useMutation({
    mutationFn: () =>
      api<RunDetail & { skippedNoSalary: string[] }>(`/schools/${schoolId}/payroll-runs`, {
        method: 'POST',
        body: { period },
      }),
    onSuccess: (run) => {
      setSkipped(run.skippedNoSalary);
      setSelected(run.id);
      setPeriod('');
      done();
    },
    onError: fail,
  });
  const act = useMutation({
    mutationFn: (args: { what: string; body?: object }) =>
      api<RunDetail & { skippedNoSalary?: string[] }>(
        `/schools/${schoolId}/payroll-runs/${selected}/${args.what}`,
        { method: 'POST', body: args.body ?? {} },
      ),
    onSuccess: (run) => {
      if (run.skippedNoSalary) setSkipped(run.skippedNoSalary);
      setReason('');
      done();
    },
    onError: fail,
  });

  const slip = async (itemId: string, name: string) => {
    try {
      saveBlob(
        await apiBlob(`/schools/${schoolId}/payslips/${itemId}/payslip.pdf`),
        `payslip-${name}.pdf`,
      );
    } catch (e) {
      fail(e);
    }
  };

  const currency = detail.data?.currency ?? runs.data?.currency ?? 'USD';
  const run = detail.data;

  return (
    <section aria-label={t('hr.tab.payroll')} className="space-y-6">
      {message && <Notice kind="error">{message}</Notice>}
      {can('payroll.prepare', schoolId) && (
        <form
          noValidate
          aria-label={t('hr.prepareRun')}
          onSubmit={(e) => {
            e.preventDefault();
            if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(period)) return setPeriodError(t('hr.period'));
            setPeriodError(null);
            create.mutate();
          }}
          className="flex flex-wrap items-end gap-3"
        >
          <TextField
            label={t('hr.period')}
            placeholder="2026-09"
            value={period}
            error={periodError}
            onChange={(e) => setPeriod(e.target.value)}
          />
          <button type="submit" className={primaryButton} disabled={create.isPending}>
            {t('hr.prepareRun')}
          </button>
        </form>
      )}

      <div className="space-y-2">
        <h2 className="text-lg font-semibold">{t('hr.runs')}</h2>
        {runs.data?.runs.length === 0 && <p className="text-slate-600">{t('hr.noRuns')}</p>}
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
          {runs.data?.runs.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
              <span>
                <strong>{r.period}</strong> · {t(`hr.runStatus.${r.status}` as MessageKey)} ·{' '}
                {t('hr.net')} {formatMinor(r.netMinor, currency)} ({r.staffCount})
              </span>
              <button
                type="button"
                className={secondaryButton}
                aria-label={`${t('hr.openRun')}: ${r.period}`}
                onClick={() => {
                  setSelected(r.id);
                  setSkipped([]);
                }}
              >
                {t('hr.openRun')}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {run && (
        <div
          aria-label={run.period}
          role="region"
          className="space-y-4 rounded-lg border border-slate-200 bg-white p-4"
        >
          <h2 className="text-lg font-semibold">
            {run.period} · {t(`hr.runStatus.${run.status}` as MessageKey)}
          </h2>
          {skipped.length > 0 && (
            <Notice kind="error">{t('hr.skipped', { names: skipped.join(', ') })}</Notice>
          )}
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase text-slate-600">
              <tr>
                <th scope="col" className="p-2">
                  {t('hr.staff')}
                </th>
                <th scope="col" className="p-2 text-right">
                  {t('hr.gross')}
                </th>
                <th scope="col" className="p-2 text-right">
                  {t('hr.lop')}
                </th>
                <th scope="col" className="p-2 text-right">
                  {t('hr.deductions')}
                </th>
                <th scope="col" className="p-2 text-right">
                  {t('hr.net')}
                </th>
                <th scope="col" className="p-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {run.items.map((i) => (
                <tr key={i.id}>
                  <th scope="row" className="p-2 font-medium">
                    {i.staffName}
                    {i.unmarkedDays > 0 && (
                      <span className="ml-2 text-xs text-amber-800">
                        ({t('hr.unmarked')}: {i.unmarkedDays})
                      </span>
                    )}
                  </th>
                  <td className="p-2 text-right">{formatMinor(i.grossMinor, currency)}</td>
                  <td className="p-2 text-right">
                    {i.lopMinor ? `${formatMinor(i.lopMinor, currency)} (${i.lopDays})` : '—'}
                  </td>
                  <td className="p-2 text-right">
                    {i.deductionsMinor ? formatMinor(i.deductionsMinor, currency) : '—'}
                  </td>
                  <td className="p-2 text-right font-medium">
                    {formatMinor(i.netMinor, currency)}
                  </td>
                  <td className="p-2 text-right">
                    {(run.status === 'approved' || run.status === 'paid') && (
                      <button
                        type="button"
                        className={secondaryButton}
                        aria-label={t('hr.payslipFor', { name: i.staffName })}
                        onClick={() => void slip(i.id, i.staffName)}
                      >
                        PDF
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex flex-wrap items-end gap-3">
            {run.status === 'draft' && can('payroll.prepare', schoolId) && (
              <button
                type="button"
                className={secondaryButton}
                disabled={act.isPending}
                onClick={() => act.mutate({ what: 'recalculate' })}
              >
                {t('hr.recalculate')}
              </button>
            )}
            {run.status === 'draft' && can('payroll.approve', schoolId) && (
              <button
                type="button"
                className={primaryButton}
                disabled={act.isPending}
                onClick={() => act.mutate({ what: 'approve' })}
              >
                {t('hr.approveRun')}
              </button>
            )}
            {run.status === 'approved' && can('payroll.pay', schoolId) && (
              <>
                <div>
                  <label htmlFor="pay-method-hr" className="block text-sm font-medium">
                    {t('hr.payMethod')}
                  </label>
                  <select
                    id="pay-method-hr"
                    value={method}
                    onChange={(e) => setMethod(e.target.value)}
                    className={selectClass}
                  >
                    {(['bank_transfer', 'cash', 'cheque'] as const).map((m) => (
                      <option key={m} value={m}>
                        {t(`hr.method.${m}` as MessageKey)}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  className={primaryButton}
                  disabled={act.isPending}
                  onClick={() => act.mutate({ what: 'pay', body: { method } })}
                >
                  {t('hr.payRun')}
                </button>
              </>
            )}
            {(run.status === 'draft' || run.status === 'approved') &&
              can('payroll.approve', schoolId) && (
                <>
                  <TextField
                    label={t('hr.cancelReason')}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <button
                    type="button"
                    className="rounded border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                    disabled={!reason.trim() || act.isPending}
                    onClick={() => act.mutate({ what: 'cancel', body: { reason } })}
                  >
                    {t('hr.cancelRun')}
                  </button>
                </>
              )}
          </div>
        </div>
      )}
    </section>
  );
}
